import apiClient from './apiClient';

// Types and interfaces
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  email: string;
  password: string;
  name: string;
  isAmbassador?: boolean;
}

export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  isAmbassador?: boolean;
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

class AuthService {
  private static instance: AuthService;
  private readonly baseUrl = '/auth';
  private readonly TOKEN_KEY = 'mocktrail_access_token';
  private readonly REFRESH_TOKEN_KEY = 'mocktrail_refresh_token';
  private readonly USER_KEY = 'mocktrail_user';

  private constructor() {
    // Set up axios interceptors for automatic token injection
    this.setupInterceptors();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Set up axios interceptors to automatically include auth token
   */
  private setupInterceptors(): void {
    // Request interceptor to add token
    apiClient['axiosInstance'].interceptors.request.use(
      (config: any) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => Promise.reject(error)
    );

    // Response interceptor to handle token expiry
    apiClient['axiosInstance'].interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            await this.refreshAccessToken();
            const newToken = this.getAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient['axiosInstance'](originalRequest);
          } catch (refreshError) {
            this.logout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      console.log('Logging in user...');
      
      const response = await apiClient.post<ApiResponse<AuthResponse>>(
        `${this.baseUrl}/login`,
        credentials
      );

      const authData = response.data;
      
      // Store tokens and user data
      this.setAccessToken(authData.accessToken);
      this.setRefreshToken(authData.refreshToken);
      this.setUser(authData.user);
      
      console.log('Login successful:', authData.user);
      return authData.user;
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Failed to login');
    }
  }

  /**
   * Register a new user
   */
  async signup(signupData: SignupData): Promise<AuthUser> {
    try {
      console.log('Registering new user...');
      
      const response = await apiClient.post<ApiResponse<AuthResponse>>(
        `${this.baseUrl}/register`,
        signupData
      );

      const authData = response.data;
      
      // Store tokens and user data
      this.setAccessToken(authData.accessToken);
      this.setRefreshToken(authData.refreshToken);
      this.setUser(authData.user);
      
      console.log('Signup successful:', authData.user);
      return authData.user;
    } catch (error: any) {
      console.error('Signup error:', error);
      throw new Error(error.message || 'Failed to create account');
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      // Call logout endpoint if we have a token
      const token = this.getAccessToken();
      if (token) {
        await apiClient.post(`${this.baseUrl}/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API call success
      this.clearAuthData();
    }
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken(): Promise<string> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post<ApiResponse<{ accessToken: string }>>(
        `${this.baseUrl}/refresh`,
        { refreshToken }
      );

      const newAccessToken = response.data.accessToken;
      this.setAccessToken(newAccessToken);
      
      return newAccessToken;
    } catch (error: any) {
      console.error('Token refresh error:', error);
      throw new Error(error.message || 'Failed to refresh token');
    }
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): AuthUser | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  /**
   * Check if current user is an ambassador
   */
  isAmbassador(): boolean {
    const user = this.getCurrentUser();
    return user?.isAmbassador || false;
  }

  /**
   * Get access token from storage
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get refresh token from storage
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Set access token in storage
   */
  private setAccessToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Set refresh token in storage
   */
  private setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  /**
   * Set user data in storage
   */
  private setUser(user: AuthUser): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Clear all auth data from storage
   */
  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  /**
   * Initialize auth state on app load
   */
  initializeAuth(): void {
    // Check if we have valid auth data
    const token = this.getAccessToken();
    const user = this.getCurrentUser();
    
    if (!token || !user) {
      this.clearAuthData();
    }
  }
}

// Export singleton instance
const authService = AuthService.getInstance();
export default authService; 